import React, { useState } from "react";
import {
  CircularProgress,
  Paper,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
} from "@mui/material";
import { CheckBox } from "components/checkBoxCommon/styles";
import {
  useCopiarProposta,
  useGetBlocosCopiaProposta,
  useGetEmpreendimentosCopiaProposta,
  useGetPropostasUnidadeCopiaProposta,
  useGetUnidadesCopiaProposta,
} from "services/api/CopiaProposta";
import { extrairIdOportunidade } from "services/api/CopiaProposta/post-copiar-proposta";
import * as Icon from "../../../../../../assets/iconsSVG";
import * as Styles from "./styles";
import SelectComponent from "../../../../../selectAutoCompleteG";

interface Option {
  label: string;
  value: string;
}

interface Props {
  onClose: () => void;
  /** Unidade destino, que receberá o fluxo copiado. */
  idUnidade?: string | null;
  idLeadCorretor?: string | null;
  /**
   * Data de entrega da unidade destino, no formato "dd/MM/yyyy HH:mm" devolvido
   * pela proposta. É usada para filtrar as origens, já que só se pode copiar de
   * empreendimento com a mesma data de entrega.
   */
  dataEntrega?: string | null;
  refetch: () => void;
  /**
   * A cópia já nasce como oportunidade. O id pode vir no POST ou no GET
   * seguinte da proposta; o concluir usa esse id para alterar.
   */
  onPropostaCopiada?: (idOportunidade: string | null) => void;
}

const OPCAO_VAZIA: Option = { label: "", value: "" };

const LINHAS_POR_PAGINA = 10;

/** Converte "dd/MM/yyyy HH:mm" — formato devolvido pela API — em "yyyy-MM-dd". */
const paraDataDeConsulta = (valor?: string | null) => {
  if (!valor) return null;
  const [dataParte] = valor.split(" ");
  const [dia, mes, ano] = dataParte.split("/");
  if (!dia || !mes || !ano) return null;
  return `${ano}-${mes.padStart(2, "0")}-${dia.padStart(2, "0")}`;
};

/** As opções dos filtros chegam como { id, nome } em todos os três endpoints. */
const paraOpcoes = (itens?: any[]): Option[] =>
  (itens || []).map((item: any) => ({
    label: item?.nome,
    value: item?.id,
  }));

const CopiarPropostaModal: React.FC<Props> = (props) => {
  const [empreendimento, setEmpreendimento] = useState<Option>(OPCAO_VAZIA);
  const [bloco, setBloco] = useState<Option>(OPCAO_VAZIA);
  const [unidade, setUnidade] = useState<Option>(OPCAO_VAZIA);
  const [propostaSelecionada, setPropostaSelecionada] = useState<string>("");
  const [pagina, setPagina] = useState(0);

  const dataEntrega = paraDataDeConsulta(props.dataEntrega);

  const { data: empreendimentos, isFetching: carregandoEmpreendimentos } =
    useGetEmpreendimentosCopiaProposta({
      dataEntrega,
      enabled: true,
    });

  const { data: blocos, isFetching: carregandoBlocos } =
    useGetBlocosCopiaProposta({
      dataEntrega,
      idEmpreendimento: empreendimento.value,
      enabled: Boolean(empreendimento.value),
    });

  const { data: unidades, isFetching: carregandoUnidades } =
    useGetUnidadesCopiaProposta({
      idBloco: bloco.value,
      enabled: Boolean(bloco.value),
    });

  const {
    data: propostas,
    isFetching: carregandoPropostas,
    isSuccess: propostasCarregadas,
  } = useGetPropostasUnidadeCopiaProposta({
    idUnidade: unidade.value,
    enabled: Boolean(unidade.value),
  });

  const { mutate, isLoading: copiando } = useCopiarProposta({
    idUnidade: props.idUnidade,
    idLeadCorretor: props.idLeadCorretor,
  });

  // Trocar um filtro invalida os seguintes, evitando combinações inconsistentes.
  const handleEmpreendimento = (valor: Option) => {
    setEmpreendimento(valor || OPCAO_VAZIA);
    setBloco(OPCAO_VAZIA);
    setUnidade(OPCAO_VAZIA);
    setPropostaSelecionada("");
    setPagina(0);
  };

  const handleBloco = (valor: Option) => {
    setBloco(valor || OPCAO_VAZIA);
    setUnidade(OPCAO_VAZIA);
    setPropostaSelecionada("");
    setPagina(0);
  };

  const handleUnidade = (valor: Option) => {
    setUnidade(valor || OPCAO_VAZIA);
    setPropostaSelecionada("");
    setPagina(0);
  };

  const todasPropostas = propostas?.propostas || [];
  const totalPropostas = propostas?.totalRegistros ?? todasPropostas.length;

  // A API devolve a lista completa da unidade; a paginação é feita no cliente.
  const listaPropostas = todasPropostas.slice(
    pagina * LINHAS_POR_PAGINA,
    pagina * LINHAS_POR_PAGINA + LINHAS_POR_PAGINA
  );

  const handleCopiar = () => {
    if (!propostaSelecionada) return;
    mutate(
      {
        idUnidadePropostaSelecionada: unidade.value,
        idPropostaSelecionada: propostaSelecionada,
      },
      {
        onSuccess: (resposta) => {
          props.onPropostaCopiada?.(extrairIdOportunidade(resposta));
          props.refetch();
          props.onClose();
        },
      }
    );
  };

  const renderCorpoTabela = () => {
    if (!unidade.value) {
      return (
        <Styles.EmptyState>
          Selecione empreendimento, bloco e unidade para listar as propostas.
        </Styles.EmptyState>
      );
    }

    if (carregandoPropostas && todasPropostas.length === 0) {
      return <Skeleton variant="rectangular" height={160} />;
    }

    if (propostasCarregadas && todasPropostas.length === 0) {
      return (
        <Styles.EmptyState>
          Nenhuma proposta elegível encontrada para esta unidade.
        </Styles.EmptyState>
      );
    }

    return (
      <>
        <TableContainer component={Paper} elevation={0}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell />
                <TableCell>Data Criação</TableCell>
                <TableCell>Valor Total</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Id Proposta</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {listaPropostas.map((proposta) => (
                <TableRow key={proposta?.propostaId}>
                  <TableCell padding="checkbox">
                    <CheckBox
                      checked={propostaSelecionada === proposta?.propostaId}
                      onChange={() =>
                        setPropostaSelecionada(
                          propostaSelecionada === proposta?.propostaId
                            ? ""
                            : proposta?.propostaId
                        )
                      }
                    />
                  </TableCell>
                  <TableCell>{proposta?.dataCriacao || "-"}</TableCell>
                  <TableCell>{proposta?.valorTotal || "-"}</TableCell>
                  <TableCell>{proposta?.status || "-"}</TableCell>
                  <TableCell>{proposta?.numeroCotacao || "-"}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        <Styles.PaginacaoRow>
          <Styles.TotalPropostas>
            Total de propostas: {totalPropostas}
          </Styles.TotalPropostas>
          <TablePagination
            component="div"
            count={totalPropostas}
            page={pagina}
            onPageChange={(_event, novaPagina) => setPagina(novaPagina)}
            rowsPerPage={LINHAS_POR_PAGINA}
            rowsPerPageOptions={[LINHAS_POR_PAGINA]}
            labelDisplayedRows={({ from, to, count }) =>
              `${from}-${to} de ${count}`
            }
          />
        </Styles.PaginacaoRow>
      </>
    );
  };

  return (
    <Styles.Container>
      <Styles.Header>
        <Styles.RowSpace>
          <Styles.Row>
            <Styles.Title>Copiar Proposta</Styles.Title>
          </Styles.Row>
          <Styles.CloseButton onClick={() => props.onClose()}>
            <Icon.CloseIcon />
          </Styles.CloseButton>
        </Styles.RowSpace>
      </Styles.Header>

      <Styles.Body>
        <Styles.Card>
          <Styles.CardTitle>Selecionar Unidade</Styles.CardTitle>
          <Styles.FiltrosRow>
            <Styles.Filtro>
              <Styles.FiltroLabel>Empreendimento:</Styles.FiltroLabel>
              <Styles.FiltroCampo>
                <SelectComponent
                  borderRadius="8px"
                  placeholder="Selecione..."
                  disabled={carregandoEmpreendimentos}
                  data={paraOpcoes(empreendimentos)}
                  value={empreendimento}
                  onChange={handleEmpreendimento}
                />
              </Styles.FiltroCampo>
            </Styles.Filtro>

            <Styles.Filtro>
              <Styles.FiltroLabel>Bloco:</Styles.FiltroLabel>
              <Styles.FiltroCampo>
                <SelectComponent
                  borderRadius="8px"
                  placeholder="Selecione..."
                  disabled={!empreendimento.value || carregandoBlocos}
                  data={paraOpcoes(blocos)}
                  value={bloco}
                  onChange={handleBloco}
                />
              </Styles.FiltroCampo>
            </Styles.Filtro>

            <Styles.Filtro flex={0.6}>
              <Styles.FiltroLabel>Unidade:</Styles.FiltroLabel>
              <Styles.FiltroCampo>
                <SelectComponent
                  borderRadius="8px"
                  placeholder="Selecione..."
                  disabled={!bloco.value || carregandoUnidades}
                  data={paraOpcoes(unidades)}
                  value={unidade}
                  onChange={handleUnidade}
                />
              </Styles.FiltroCampo>
            </Styles.Filtro>
          </Styles.FiltrosRow>
        </Styles.Card>

        <Styles.Card>
          <Styles.RowSpace>
            <Styles.CardTitle>Propostas da Unidade</Styles.CardTitle>
            <Styles.ButtonBlue
              disabled={!propostaSelecionada || copiando}
              onClick={handleCopiar}
            >
              {copiando ? (
                <CircularProgress size={22} style={{ color: "#fff" }} />
              ) : (
                <Styles.LightText>Copiar Proposta</Styles.LightText>
              )}
            </Styles.ButtonBlue>
          </Styles.RowSpace>
          {renderCorpoTabela()}
        </Styles.Card>
      </Styles.Body>
    </Styles.Container>
  );
};

export default CopiarPropostaModal;
